import { Button, Group, Loader, Stack, Text, Title, Paper, Divider } from '@mantine/core';
import { IconPrinter, IconRefresh } from '@tabler/icons-react';
import { useWebsitePages } from '@/hooks/useWebsite';

export function WebsitePrintPage() {
  const { data: pages, isLoading, isFetching, refetch } = useWebsitePages();

  return (
    <Stack gap="md">
      <Group justify="space-between" className="no-print">
        <Title order={2}>Website Druckversion</Title>
        <Group>
          <Button
            leftSection={<IconRefresh size={16} />}
            variant="light"
            onClick={() => refetch()}
            loading={isFetching}
          >
            Aktualisieren
          </Button>
          <Button leftSection={<IconPrinter size={16} />} onClick={() => window.print()}>
            Drucken
          </Button>
        </Group>
      </Group>

      {isLoading && (
        <Stack align="center" py="xl">
          <Loader />
          <Text size="sm" c="dimmed">
            Website wird geladen...
          </Text>
        </Stack>
      )}

      {pages && pages.length === 0 && (
        <Text c="dimmed">Keine Seiten gefunden.</Text>
      )}

      {pages && pages.length > 0 && (
        <Paper p="xl" className="print-content">
          <Stack gap="xl">
            {pages.map((page, index) => (
              <div key={page.id}>
                {index > 0 && <Divider my="xl" className="no-print" />}
                <div className="print-page-break">
                  <Title order={2} mb="md">
                    {page.title}
                  </Title>
                  <Text size="xs" c="dimmed" mb="sm" className="no-print">
                    {page.url}
                  </Text>
                  <div
                    className="website-content"
                    dangerouslySetInnerHTML={{ __html: page.content }}
                  />
                </div>
              </div>
            ))}
          </Stack>
        </Paper>
      )}

      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-page-break { page-break-before: always; }
          .print-page-break:first-child { page-break-before: avoid; }
          .print-content { box-shadow: none !important; border: none !important; padding: 0 !important; }
          body { font-size: 11pt; }
          .website-content img { max-height: 150px !important; }
        }
        .website-content img {
          max-width: 220px;
          max-height: 180px;
          height: auto;
          width: auto;
          object-fit: contain;
          display: inline-block;
          vertical-align: top;
          margin: 4px;
        }
        /* App store badges smaller */
        .website-content img[alt*="App Store"],
        .website-content img[alt*="Google Play"] {
          max-width: 120px;
          max-height: 40px;
        }
        .website-content video,
        .website-content audio { display: none; }
        .website-content table { width: 100%; border-collapse: collapse; }
        .website-content table td,
        .website-content table th { padding: 8px; border: 1px solid #ddd; }
        /* Arrange image containers as flex rows */
        .website-content figure,
        .website-content [class*="gallery"],
        .website-content [class*="screenshot"],
        .website-content [class*="app-store"],
        .website-content [class*="image"] {
          display: inline-block;
          vertical-align: top;
        }
        /* Make sections with multiple images flow horizontally */
        .website-content div:has(> img + img),
        .website-content div:has(> figure + figure),
        .website-content div:has(> div > img) {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          align-items: flex-start;
        }
        /* Ensure all child containers with images also flow inline */
        .website-content div:has(img) {
          flex-wrap: wrap;
        }
        /* Hide decorative/interactive elements */
        .website-content [class*="swiper"],
        .website-content [class*="slider"],
        .website-content [class*="carousel"] {
          overflow: visible !important;
          animation: none !important;
          white-space: normal !important;
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }
        /* Force everything visible — no collapsed/hidden content */
        .website-content * {
          visibility: visible !important;
          opacity: 1 !important;
          max-height: none !important;
          overflow: visible !important;
        }
        .website-content [role="tabpanel"],
        .website-content details > *,
        .website-content [class*="accordion"] [class*="content"],
        .website-content [class*="accordion"] [class*="panel"],
        .website-content [class*="faq"] [class*="answer"],
        .website-content [class*="faq"] [class*="content"],
        .website-content [class*="collapse"],
        .website-content [class*="hidden"],
        .website-content [class*="closed"] {
          display: block !important;
          position: static !important;
          height: auto !important;
          transform: none !important;
          clip: auto !important;
          clip-path: none !important;
        }
      `}</style>
    </Stack>
  );
}
